import React, { useState, useRef } from 'react';
import CustomerSelector from '../../components/CustomerSelector/CustomerSelector';
import serviceCall from '../../../src/store/serviceCall';
import saveAs from 'file-saver';
import Form, {
  Item,
  Label,
  ButtonItem,
  ButtonOptions,
  RequiredRule,
} from 'devextreme-react/form';
import { LoadPanel } from 'devextreme-react/load-panel';
import { ToasterMessage } from '../../../src/components/sharedComponents';
import './AIHubLink.scss';

export default function AIHubLink(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [isCustomerSelected, setIsCustomerSelected] = useState(false);
  const [formData, setFormData] = useState({
    CustomerId: '',
    ClientId: '',
    ClientSecret: '',
    AuthUrl: '',
    ApiUrl: '',
  });
  const importFileRef = useRef<HTMLInputElement>(null);

  const handleCustomerChange = async (CustomerId: string): Promise<void> => {
    setFormData(prevFormData => ({
      ...prevFormData,
      CustomerId,
      ClientId: '',
      ClientSecret: '',
      AuthUrl: '',
      ApiUrl: '',
    }));
    setIsLoading(true);
    setIsCustomerSelected(true);
    try {
      const response = await serviceCall.getData(
        `AIHub/GetPublicConnectionDetails/${CustomerId}`,
      );
      if (response.status && response.status === 200 && response.data) {
        setFormData(prevFormData => ({
          ...prevFormData,
          ClientId: response.data.clientId || '',
          ClientSecret: '',
          AuthUrl: response.data.authUrl || '',
          ApiUrl: response.data.apiUrl || '',
        }));
      }
    } catch (error) {
      ToasterMessage('error', 'Error when getting connection details.');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await serviceCall
        .postData('AIHub/UpdateAIHubConnectionDetails', formData)
        .then(res => {
          if (res.status && res.status === 200) {
            ToasterMessage('success', 'Customer linked to AIHub successfully.');
          } else {
            ToasterMessage('error', 'Please check connection details.');
          }
        });
    } catch (error) {
      console.error('Error:', error);
      ToasterMessage(
        'error',
        'Error during connection attempt. Please check the logs.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onDelete = async (): Promise<void> => {
    const confirmed = window.confirm(
      'Are you sure you want to delete the connection for this customer?',
    );
    try {
      if (!confirmed) {
        return;
      }
      await serviceCall.deleteData(
        'AIHub/DeleteAIHubConnectionDetails',
        formData,
      );
      setFormData(prevFormData => ({
        ...prevFormData,
        ClientId: '',
        ClientSecret: '',
        AuthUrl: '',
        ApiUrl: '',
      }));
      ToasterMessage(
        'success',
        'Customer connection details have been deleted',
      );
    } catch (error) {
      console.error('Error:', error);
      ToasterMessage(
        'error',
        `Could not delete the customer details - ${error}`,
      );
    }
  };

  const onExport = async (): Promise<void> => {
    try {
      setIsLoading(true);

      const res = await serviceCall.postData(
        'AIHub/ExportMachineMapping',
        formData,
      );

      if (res.status === 200) {
        saveAs(
          new Blob([res.data], { type: 'text/csv;charset=utf-8' }),
          'MachineExport.csv',
        );
        ToasterMessage(
          'success',
          'Exported machine mapping - check your downloads',
        );
      } else {
        ToasterMessage(
          'error',
          'Something went wrong - check the dev console.',
        );
      }
    } catch (error) {
      console.info('Error:', error);
      ToasterMessage('error', 'Error during export. Please check the logs.');
    } finally {
      setIsLoading(false);
    }
  };

  const onClickImport = async (): Promise<void> => {
    if (!importFileRef || !importFileRef.current) {
      return;
    }
    importFileRef.current.value = ''; // Reset the input value
    importFileRef.current.click();
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    event.preventDefault();
    try {
      setIsLoading(true);

      const files = event.target.files;
      if (!files) {
        ToasterMessage('error', 'No file selected');
        return;
      }

      const file = files[0];
      const formData = new FormData();
      formData.append('file', file);

      const response = await serviceCall.postData(
        'AiHub/ImportMachineMapping',
        formData,
      );

      if (response.status && response.status === 200) {
        ToasterMessage('success', 'Machine mapping updated');
      } else {
        ToasterMessage('error', 'File upload failed');
      }
    } catch (error) {
      ToasterMessage('error', 'Error during file upload');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="AIHubLinkForm" autoComplete="off">
      <input
        type="file"
        hidden
        ref={importFileRef}
        accept=".csv"
        onChange={handleFileUpload}
      />
      <div className="FormColumn">
        <Form formData={formData}>
          <Item dataField="CustomerId">
            <Label text="Customer" />
            <CustomerSelector
              selectedCustomerId={formData.CustomerId}
              onCustomerChange={e => handleCustomerChange(e.value)}
            />
          </Item>
          <ButtonItem>
            <ButtonOptions
              text="Export equipment mapping"
              type="normal"
              width="100%"
              onClick={onExport}
              disabled={!isCustomerSelected}
            />
          </ButtonItem>
          <ButtonItem>
            <ButtonOptions
              text="Import equipment mapping"
              type="normal"
              width="100%"
              onClick={onClickImport}
              disabled={!isCustomerSelected}
            />
          </ButtonItem>
          <ButtonItem>
            <ButtonOptions
              text="Delete Connection Details"
              type="danger"
              width="100%"
              onClick={onDelete}
              disabled={!isCustomerSelected}
            />
          </ButtonItem>
          {isLoading && <LoadPanel visible={true} />}
        </Form>
      </div>
      <div className="FormColumn">
        <Form formData={formData}>
          <Item
            dataField="ClientId"
            editorType="dxTextBox"
            editorOptions={{
              attr: { autocomplete: 'off' },
              disabled: !isCustomerSelected,
            }}>
            <Label text="Client ID" />
            <RequiredRule message="Client ID is required" />
          </Item>
          <Item
            dataField="ClientSecret"
            editorType="dxTextBox"
            editorOptions={{
              mode: 'password',
              attr: { autocomplete: 'off' },
              disabled: !isCustomerSelected,
            }}>
            <Label text="Client Secret" />
            <RequiredRule message="Client Secret is required" />
          </Item>
          <Item
            dataField="AuthUrl"
            editorType="dxTextBox"
            editorOptions={{
              disabled: !isCustomerSelected,
            }}>
            <Label text="Cognito Auth URL" />
            <RequiredRule message="Auth URL is required" />
          </Item>
          <Item
            dataField="ApiUrl"
            editorType="dxTextBox"
            editorOptions={{
              disabled: !isCustomerSelected,
            }}>
            <Label text="Customer AIHub API URL" />
            <RequiredRule message="AIHub API URL is required" />
          </Item>
          <ButtonItem>
            <ButtonOptions
              text="Submit"
              type="default"
              width="100%"
              onClick={onSubmit}
              disabled={!isCustomerSelected}
            />
          </ButtonItem>
          {isLoading && <LoadPanel visible={true} />}
        </Form>
      </div>
    </form>
  );
}
